import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface PendingPhotosModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PendingPhotosModal: React.FC<PendingPhotosModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [photos, setPhotos] = useState<{ front: string | null; side: string | null; back: string | null }>({
        front: null,
        side: null,
        back: null
    });

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activePhotoType, setActivePhotoType] = useState<'front' | 'side' | 'back' | null>(null);

    if (!isOpen) return null;

    const compressImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = objectUrl;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 800;
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);

                try {
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
                    URL.revokeObjectURL(objectUrl);
                    resolve(dataUrl);
                } catch (err) {
                    URL.revokeObjectURL(objectUrl);
                    reject(err);
                }
            };
            img.onerror = (err) => {
                URL.revokeObjectURL(objectUrl);
                reject(err);
            };
        });
    };

    const handlePhotoUpload = (type: 'front' | 'side' | 'back') => {
        setActivePhotoType(type);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && activePhotoType) {
            try {
                const compressed = await compressImage(file);
                setPhotos(prev => ({ ...prev, [activePhotoType]: compressed }));
            } catch (error) {
                console.error("Compression failed:", error);
                alert("No se pudo procesar la foto. Si la imagen es muy pesada o de iCloud, intenta hacerle una captura de pantalla e intentarlo con esa.");
            }
        }
    };

    const handleSubmit = async () => {
        if (!user) return;
        if (!photos.front || !photos.side || !photos.back) {
            alert(t('checkin.alert_photos', "Por favor, sube las tres fotos corporales."));
            return;
        }

        setIsSubmitting(true);

        try {
            const uploadPhoto = async (base64: string, angle: string) => {
                if (!base64 || !base64.startsWith('data:image')) return base64;
                const res = await fetch(base64);
                const blob = await res.blob();
                const filePath = `${user.id}/${Date.now()}_${angle}.jpg`;
                const { error } = await supabase.storage.from('checkins').upload(filePath, blob, { contentType: 'image/jpeg' });
                if (error) {
                    console.error(`[Storage] Failed to upload ${angle} photo:`, error);
                    throw new Error(`No se pudo subir la foto ${angle}: ${error.message}`);
                }
                const { data } = supabase.storage.from('checkins').getPublicUrl(filePath);
                return data.publicUrl;
            };

            const finalPhotos = {
                front: photos.front ? await uploadPhoto(photos.front, 'front') : null,
                side: photos.side ? await uploadPhoto(photos.side, 'side') : null,
                back: photos.back ? await uploadPhoto(photos.back, 'back') : null
            };

            // Fetch current profile stats
            const { data: profile, error: fetchError } = await supabase.from('profiles').select('stats').eq('id', user.id).single();
            if (fetchError) {
                throw new Error(`Error al consultar perfil: ${fetchError.message}`);
            }
            const currentStats = profile?.stats || {};

            // V7008: Save baseline photos as the initial weekly checkin
            const userWeight = currentStats.weight || currentStats.initialWeight || 0;
            const initialCheckin = {
                user_id: user.id,
                date: new Date().toISOString(),
                weight: typeof userWeight === 'string' ? parseFloat(userWeight) : userWeight,
                photos: finalPhotos,
                notes: 'Registro de fotos iniciales al comenzar el programa.'
            };
            
            const { error: insertError } = await supabase.from('weekly_checkins').insert(initialCheckin);
            if (insertError) {
                throw new Error(`Error al guardar check-in inicial: ${insertError.message}`);
            }

            const startTime = Date.now();
            const targetTime = startTime + 90 * 60 * 1000; // 90 min target

            const updatedStats = {
                ...currentStats,
                photos: finalPhotos,
                aura_diet_status: 'generating_initial',
                aura_diet_process_start: startTime,
                aura_target_completion_time: targetTime,
                aura_generation_type: 'new'
            };

            // Update DB
            const { error: updateError } = await supabase.from('profiles').update({
                stats: updatedStats,
                aura_diet_status: 'generating_initial',
                aura_diet_process_start: startTime,
                aura_target_completion_time: targetTime
            }).eq('id', user.id);

            if (updateError) {
                throw new Error(`Error al actualizar perfil: ${updateError.message}`);
            }

            // Sync to local storage checkin strip (strip base64 if upload failed to keep localStorage lightweight)
            const cleanPhotosForLocal = {
                front: finalPhotos.front && finalPhotos.front.startsWith('data:image') ? 'local-fallback' : finalPhotos.front,
                side: finalPhotos.side && finalPhotos.side.startsWith('data:image') ? 'local-fallback' : finalPhotos.side,
                back: finalPhotos.back && finalPhotos.back.startsWith('data:image') ? 'local-fallback' : finalPhotos.back
            };

            const localCheckin = {
                id: Date.now().toString(),
                date: initialCheckin.date,
                weight: initialCheckin.weight,
                photos: cleanPhotosForLocal,
                notes: initialCheckin.notes
            };
            const existingRaw = localStorage.getItem('aura_weekly_checkins');
            let existingCheckins = [];
            try {
                if (existingRaw) existingCheckins = JSON.parse(existingRaw) || [];
            } catch(e) {}
            
            try {
                // Keep only the 4 most recent checkins local to stay under quota
                const limitedCheckins = [localCheckin, ...existingCheckins].map((c, i) => {
                    if (i < 4) return c;
                    return { ...c, photos: undefined };
                });
                localStorage.setItem('aura_weekly_checkins', JSON.stringify(limitedCheckins));
            } catch (e) {
                console.warn("Could not save to local storage checkins (quota exceeded):", e);
            }

            // Sync Local Storage
            try {
                const localStats = {
                    ...updatedStats,
                    photos: cleanPhotosForLocal
                };
                localStorage.setItem('aura_user_data', JSON.stringify(localStats));
            } catch (e) {
                console.warn("Could not save user data to local storage (quota exceeded):", e);
            }

            try {
                localStorage.setItem('aura_diet_status', 'generating_initial');
                localStorage.setItem('aura_diet_process_start', startTime.toString());
                localStorage.setItem('aura_target_completion_time', targetTime.toString());
                localStorage.setItem('aura_generation_type', 'new');
            } catch (e) {
                console.warn("Could not save diet metadata to local storage:", e);
            }

            // Dispatch global event
            window.dispatchEvent(new Event('aura_diet_updated'));

            // Invoke generate-diet Edge Function
            supabase.functions.invoke('generate-diet', {
                body: { user_id: user.id },
                headers: { 'x-manual-invoke': 'true' }
            }).catch(console.error);

            // Flags for Marc's welcome
            localStorage.setItem('aura_pending_welcome_sequence', 'true');
            localStorage.setItem('aura_force_welcome', 'true');

            onClose();
        } catch (e: any) {
            console.error("Error submitting photos:", e);
            alert(e.message || "Ocurrió un error al subir las fotos. Por favor, inténtalo de nuevo.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileChange}
            />
            <div className="relative max-w-md w-full bg-[#161618] border border-white/10 p-6 rounded-[2.5rem] shadow-2xl flex flex-col gap-6 text-center">
                
                {/* Close Button */}
                <button 
                    onClick={onClose} 
                    disabled={isSubmitting}
                    className="absolute top-4 right-4 size-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors hover:bg-white/10"
                >
                    <span className="material-symbols-outlined text-sm">close</span>
                </button>

                {/* Header */}
                <div className="space-y-2">
                    <div className="size-12 rounded-2xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-orange-500 mx-auto">
                        <span className="material-symbols-outlined font-black text-xl">photo_camera</span>
                    </div>
                    <h2 className="text-xl font-black text-white uppercase tracking-wider">
                        {t('subscription.photos_pending_title', 'COMPLETA TU PERFIL: SUBE TUS FOTOS')}
                    </h2>
                    <p className="text-xs text-text-muted">
                        {t('subscription.photos_pending_desc', 'Marc Serrano necesita tu estado físico actual (frente, perfil y espalda) para personalizar tus macros y activar tu plan de dieta.')}
                    </p>
                </div>

                {/* Photo Selectors */}
                <div className="grid grid-cols-3 gap-3 my-2">
                    {(['front', 'side', 'back'] as const).map((type) => (
                        <div key={type} className="flex flex-col gap-1.5 items-center">
                            <button
                                onClick={() => handlePhotoUpload(type)}
                                disabled={isSubmitting}
                                className={`w-full aspect-[3/4] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1.5 overflow-hidden bg-white/5 transition-all relative group ${
                                    photos[type] ? 'border-primary shadow-[0_0_15px_rgba(242,127,13,0.25)]' : 'border-white/10 hover:border-primary/40 hover:bg-white/[0.02]'
                                }`}
                            >
                                {photos[type] ? (
                                    <img src={photos[type]!} className="w-full h-full object-cover animate-fade-in" alt={type} />
                                ) : (
                                    <>
                                        {/* Silhouette Visual Guide */}
                                        <img 
                                            src={type === 'front' ? '/mock_front.png' : type === 'side' ? '/mock_side.png' : '/mock_back.png'} 
                                            className="absolute inset-0 w-full h-full object-cover opacity-[0.12] pointer-events-none select-none mix-blend-screen"
                                            alt="Guide silhouette"
                                        />
                                        <span className="material-symbols-outlined text-white/40 text-lg group-hover:scale-110 transition-transform relative z-10">add_a_photo</span>
                                        <span className="text-[8px] font-bold text-white/50 uppercase tracking-widest relative z-10">
                                            {t(`checkin.photo_${type}`, type)}
                                        </span>
                                    </>
                                )}
                            </button>
                            {photos[type] && !isSubmitting && (
                                <button
                                    onClick={() => setPhotos(prev => ({ ...prev, [type]: null }))}
                                    className="text-[9px] uppercase tracking-widest font-black text-red-500/70 hover:text-red-500 transition-colors"
                                >
                                    {t('common.delete', 'Quitar')}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {/* Submit CTA */}
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !photos.front || !photos.side || !photos.back}
                    className="w-full relative overflow-hidden py-4 bg-neutral-900 text-white font-black uppercase tracking-[0.2em] rounded-2xl transition-all shadow-[0_10px_30px_rgba(242,127,13,0.15)] hover:shadow-[0_20px_50px_rgba(242,127,13,0.35)] active:scale-98 text-[11px] flex items-center justify-center gap-2 border border-primary/40 cursor-pointer disabled:opacity-20 disabled:pointer-events-none disabled:shadow-none group"
                >
                    {isSubmitting ? (
                        <>
                            <span className="animate-spin material-symbols-outlined text-sm">sync</span>
                            {t('common.uploading', 'SUBIENDO...')}
                        </>
                    ) : (
                        <>
                            {/* Pulsing Aura Border */}
                            {(!isSubmitting && photos.front && photos.side && photos.back) && (
                                <div className="absolute inset-0 rounded-2xl border-2 border-primary/20 animate-pulse-slow pointer-events-none"></div>
                            )}

                            <span className="relative z-10 bg-gradient-to-r from-white via-white to-primary/45 bg-clip-text text-transparent">
                                {t('subscription.submit_photos', 'ENVIAR FOTOS Y EMPEZAR')}
                            </span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
